import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  X,
  Phone,
  Mail,
  MessageCircle,
  Search,
  User,
  Loader2,
  Link2,
  Briefcase,
  Send,
  AlertCircle,
  PhoneCall,
  ArrowRight,
} from "lucide-react";
import {
  conversationsApi,
  contactsApi,
  clientsApi,
  twilioApi,
  mattersApi,
  emailConnectionsApi,
  messagesApi,
  PaginatedResponse,
} from "../../services/api";
import SearchableSelect from "../ui/SearchableSelect";
import PremiumModal from "../ui/PremiumModal";
import QuickCreateMatterModal from "../ui/QuickCreateMatterModal";
import { useEmailCompose } from "../../contexts/EmailComposeContext";
import { formatPhoneNumber } from "../../utils/formatPhone";

interface Participant {
  kind: string;
  userId?: string;
  contactId?: string;
  displayName?: string;
  email?: string;
  phoneE164?: string;
  role?: string;
  lastReadAt?: string;
}

interface Conversation {
  _id: string;
  clientId?: string;
  matterId?: string;
  subject?: string;
  preview?: string;
  tags: string[];
  participants: Participant[];
  channels: {
    primaryChannel: string;
    inboundPhoneE164?: string;
    fromPhoneE164?: string;
    inboundEmail?: string;
    fromEmail?: string;
  };
  status: string;
  lastMessageAt?: string;
  counts: {
    totalMessages: number;
    unreadMessages: number;
  };
  createdAt: string;
}

interface NewConversationModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (conversation: Conversation) => void;
  /** Preselect this client as the recipient (e.g. the SMS button on a matter) */
  initialRecipient?: Contact | null;
  initialChannel?: string;
}

interface Contact {
  _id: string;
  displayName: string;
  emails?: { label: string; email: string }[];
  phones?: { label: string; number: string }[];
}

interface TwilioNumber {
  _id: string;
  phoneE164: string;
  friendlyName?: string;
  provisioning: { status: string };
}

interface Matter {
  _id: string;
  title?: string;

  clientId?: string;
}

interface EmailConn {
  _id: string;
  mailbox: { email: string; displayName?: string };
  provider: string;
}

// Recipient can be a contact from search OR a manually typed number/email
interface Recipient {
  type: "contact" | "manual";
  displayName: string;
  contactId?: string;
  phoneE164?: string;
  email?: string;
}

const channelOptions = [
  { key: "sms", label: "SMS", icon: Phone },
  { key: "whatsapp", label: "WhatsApp", icon: MessageCircle },
  { key: "email", label: "Email", icon: Mail },
  { key: "voice", label: "Call", icon: PhoneCall },
];

// Check if input looks like a phone number
const looksLikePhone = (input: string) => {
  const digits = input.replace(/\D/g, "");
  return digits.length >= 7 && /^[+\d\s()-]+$/.test(input.trim());
};

// Check if input looks like an email
const looksLikeEmail = (input: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.trim());

// Normalize a phone input to E.164
const toE164 = (input: string): string => {
  const digits = input.replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  if (input.startsWith("+")) return `+${digits}`;
  return `+${digits}`;
};

const NewConversationModal: React.FC<NewConversationModalProps> = ({ open, onClose, onCreated, initialRecipient, initialChannel }) => {
  const navigate = useNavigate();
  const emailCompose = useEmailCompose();

  // Channel
  const [channel, setChannel] = useState("sms");

  // Recipient
  const [toInput, setToInput] = useState("");
  const [recipient, setRecipient] = useState<Recipient | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [contactLoading, setContactLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const contactSearchRef = useRef<string>("");
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // From number / email
  const [twilioNumbers, setTwilioNumbers] = useState<TwilioNumber[]>([]);
  const [numbersLoaded, setNumbersLoaded] = useState(false);
  const [selectedFromNumber, setSelectedFromNumber] = useState("");
  const [emailConnections, setEmailConnections] = useState<EmailConn[]>([]);
  const [selectedEmailConnectionId, setSelectedEmailConnectionId] = useState<string | null>(null);

  // Matter
  const [clientMatters, setClientMatters] = useState<Matter[]>([]);
  const [selectedMatter, setSelectedMatter] = useState("");
  const [mattersLoading, setMattersLoading] = useState(false);
  const [showQuickMatter, setShowQuickMatter] = useState(false);
  const [showMatterField, setShowMatterField] = useState(false);

  // Message
  const [messageBody, setMessageBody] = useState("");
  const [subject, setSubject] = useState("");
  const [creating, setCreating] = useState(false);
  const [errorBanner, setErrorBanner] = useState("");

  // Reset on open
  useEffect(() => {
    if (open) {
      setChannel(initialChannel || "sms");
      setToInput("");
      setRecipient(
        initialRecipient
          ? {
              type: "contact",
              displayName: initialRecipient.displayName,
              contactId: initialRecipient._id,
              phoneE164: initialRecipient.phones?.[0]?.number || "",
              email: initialRecipient.emails?.[0]?.email || "",
            }
          : null
      );
      setContacts([]);
      setSelectedFromNumber("");
      setSelectedEmailConnectionId(null);
      setSelectedMatter("");
      setClientMatters([]);
      setMessageBody("");
      setSubject("");
      setErrorBanner("");
      setShowMatterField(false);
      setShowResults(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Fetch Twilio numbers + email connections on open
  useEffect(() => {
    if (!open) return;
    setNumbersLoaded(false);
    twilioApi
      .listNumbers({ status: "active", limit: 50 })
      .then((res) => {
        const data = res.data as PaginatedResponse<TwilioNumber>;
        setTwilioNumbers(data.data || []);
        if (data.data?.length > 0) {
          setSelectedFromNumber(data.data[0].phoneE164);
        }
      })
      .catch(() => {})
      .finally(() => setNumbersLoaded(true));

    emailConnectionsApi
      .list()
      .then((res) => {
        const connections = res.data?.data || res.data || [];
        if (Array.isArray(connections) && connections.length > 0) {
          setEmailConnections(connections);
          setSelectedEmailConnectionId(connections[0]._id);
        }
      })
      .catch(() => {});
  }, [open]);

  // Debounced contact search
  const searchContacts = useCallback(async (q: string) => {
    if (!q.trim() || q.trim().length < 2) {
      setContacts([]);
      setContactLoading(false);
      return;
    }
    try {
      const [contactsRes, clientsRes] = await Promise.allSettled([
        contactsApi.list({ search: q, limit: 8 }),
        clientsApi.list({ search: q, limit: 8 }),
      ]);
      if (contactSearchRef.current !== q) return;
      const contactResults =
        contactsRes.status === "fulfilled"
          ? (contactsRes.value.data as PaginatedResponse<Contact>).data || []
          : [];
      const clientResults =
        clientsRes.status === "fulfilled"
          ? (clientsRes.value.data as PaginatedResponse<Contact>).data || []
          : [];
      const seen = new Set<string>();
      const merged: Contact[] = [];
      for (const c of [...clientResults, ...contactResults]) {
        if (!seen.has(c._id)) {
          seen.add(c._id);
          merged.push(c);
        }
      }
      setContacts(merged);
      setShowResults(true);
    } catch {
      setContacts([]);
    } finally {
      setContactLoading(false);
    }
  }, []);

  useEffect(() => {
    contactSearchRef.current = toInput;
    if (toInput.trim().length >= 2) {
      setContactLoading(true);
    }
    const timer = setTimeout(() => searchContacts(toInput), 300);
    return () => clearTimeout(timer);
  }, [toInput, searchContacts]);

  // Fetch matters when a contact is selected
  useEffect(() => {
    if (!recipient?.contactId) {
      setClientMatters([]);
      return;
    }
    setMattersLoading(true);
    mattersApi
      .list({ clientId: recipient.contactId, limit: 50 })
      .then((res) => {
        const data = res.data as PaginatedResponse<Matter>;
        setClientMatters(data.data || []);
      })
      .catch(() => setClientMatters([]))
      .finally(() => setMattersLoading(false));
  }, [recipient?.contactId]);

  // Close results on outside click
  useEffect(() => {
    if (!showResults) return;
    const handleClick = (e: MouseEvent) => {
      if (
        resultsRef.current &&
        !resultsRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showResults]);

  // Select a contact from search results
  const selectContact = (c: Contact) => {
    const phone = c.phones?.[0]?.number || "";
    const email = c.emails?.[0]?.email || "";
    setRecipient({
      type: "contact",
      displayName: c.displayName,
      contactId: c._id,
      phoneE164: phone,
      email,
    });
    setToInput("");
    setContacts([]);
    setShowResults(false);
    setErrorBanner("");
  };

  // Apply manual input as recipient
  const applyManualInput = (input: string) => {
    const trimmed = input.trim();
    if (looksLikePhone(trimmed)) {
      const e164 = toE164(trimmed);
      setRecipient({
        type: "manual",
        displayName: formatPhoneNumber(e164),
        phoneE164: e164,
      });
    } else if (looksLikeEmail(trimmed)) {
      setRecipient({
        type: "manual",
        displayName: trimmed,
        email: trimmed,
      });
    }
    setToInput("");
    setContacts([]);
    setShowResults(false);
    setErrorBanner("");
  };

  // Get the "to" address
  const getToAddress = (): string => {
    if (!recipient) return "";
    if (channel === "email") return recipient.email || "";
    return recipient.phoneE164 || "";
  };

  // Can we send?
  const isPhoneChannel = channel === "sms" || channel === "whatsapp" || channel === "voice";
  const toAddress = getToAddress();
  const canSend =
    recipient &&
    toAddress &&
    (channel === "voice" || messageBody.trim()) &&
    (isPhoneChannel ? selectedFromNumber : true);

  // Handle send
  const handleCreate = async () => {
    if (!recipient || !toAddress) return;
    setCreating(true);
    setErrorBanner("");

    try {
      // For email channel, create conversation then open email compose popup
      if (channel === "email") {
        const convRes = await conversationsApi.create({
          subject: subject || `Email with ${recipient.displayName}`,
          matterId: selectedMatter || null,
          participants: [
            {
              kind: "contact",
              contactId: recipient.contactId || undefined,
              displayName: recipient.displayName,
              phoneE164: recipient.phoneE164 || null,
              email: recipient.email || null,
              role: "primary",
            },
          ],
          channels: {
            primaryChannel: "email",
            inboundEmail: toAddress,
          },
        });
        const conversation = convRes.data as Conversation;

        emailCompose.openCompose({
          to: [toAddress],
          subject: subject || undefined,
          conversationId: conversation._id,
          emailConnectionId: selectedEmailConnectionId || undefined,
          clientId: recipient.contactId || undefined,
        });

        onCreated(conversation);
        return;
      }

      // For voice, create conversation and initiate call (no message needed)
      const convRes = await conversationsApi.create({
        subject: `${channel === "whatsapp" ? "WhatsApp" : channel === "voice" ? "Call" : "SMS"} with ${recipient.displayName}`,
        matterId: selectedMatter || null,
        participants: [
          {
            kind: "contact",
            contactId: recipient.contactId || undefined,
            displayName: recipient.displayName,
            phoneE164: recipient.phoneE164 || null,
            email: recipient.email || null,
            role: "primary",
          },
        ],
        channels: {
          primaryChannel: channel,
          inboundPhoneE164: toAddress,
          fromPhoneE164: selectedFromNumber,
        },
      });

      const conversation = convRes.data as Conversation;

      // Send first message (SMS/WhatsApp only)
      if (channel !== "voice" && messageBody.trim()) {
        try {
          await messagesApi.send({
            conversationId: conversation._id,
            channel,
            to: toAddress,
            from: selectedFromNumber || undefined,
            body: messageBody,
          });
        } catch {
          // Conversation created even if first message fails
        }
      }

      onCreated(conversation);
    } catch (err: any) {
      const data = err?.response?.data;
      // Zod validation errors come as an array of { path, message }
      let msg = "";
      if (Array.isArray(data?.errors)) {
        msg = data.errors.map((e: any) => `${e.path?.join(".") || "field"}: ${e.message}`).join("; ");
      } else if (data?.message) {
        msg = data.message;
      } else if (data?.error) {
        msg = data.error;
      } else {
        msg = `Failed to create conversation (${err?.response?.status || "unknown error"})`;
      }
      setErrorBanner(msg);
    } finally {
      setCreating(false);
    }
  };

  // Handle Enter key in "To" field
  const handleToKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const trimmed = toInput.trim();
      if (looksLikePhone(trimmed) || looksLikeEmail(trimmed)) {
        applyManualInput(trimmed);
      } else if (contacts.length > 0) {
        selectContact(contacts[0]);
      }
    }
  };

  const needsPhone = isPhoneChannel;
  const needsEmail = channel === "email";
  const isManualPhone = looksLikePhone(toInput.trim());
  const isManualEmail = looksLikeEmail(toInput.trim());
  const showManualOption = needsPhone ? isManualPhone : needsEmail ? isManualEmail : isManualPhone || isManualEmail;

  const matterOptions = clientMatters.map((m) => ({
    value: m._id,
    label: m.title || m._id,
    icon: Briefcase,
  }));

  // Format the from number label nicely — avoid showing number twice
  const formatFromLabel = (n: TwilioNumber) => {
    const formatted = formatPhoneNumber(n.phoneE164);
    if (!n.friendlyName || n.friendlyName === n.phoneE164) {
      return formatted;
    }
    return `${n.friendlyName} — ${formatted}`;
  };

  return (
    <>
      <PremiumModal
        open={open}
        onClose={onClose}
        title="New Conversation"
        subtitle="Send a message, email, or start a call"
        icon={MessageCircle}
      >
        <div className="space-y-4">
          {/* Error banner */}
          {errorBanner && (
            <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-red-50 border border-red-200">
              <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
              <span className="text-sm text-red-700 flex-1">{errorBanner}</span>
              <button
                onClick={() => setErrorBanner("")}
                className="p-0.5 rounded text-red-400 hover:text-red-600 flex-shrink-0"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}

          {/* Channel selector — compact pills */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Channel</label>
            <div className="flex gap-1.5">
              {channelOptions.map((opt) => {
                const Icon = opt.icon;
                const isActive = channel === opt.key;
                return (
                  <button
                    key={opt.key}
                    onClick={() => {
                      setChannel(opt.key);
                      setErrorBanner("");
                    }}
                    className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-medium transition-all ${
                      isActive
                        ? "text-white shadow-sm"
                        : "text-slate-600 bg-slate-100 hover:bg-slate-200"
                    }`}
                    style={isActive ? { backgroundColor: "var(--color-primary)" } : {}}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* To field — search contacts or type number/email */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">To</label>
            {recipient ? (
              <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-50 border border-slate-200">
                <div
                  className="h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
                  style={{ backgroundColor: "var(--color-accent)" }}
                >
                  {recipient.type === "contact" ? (
                    recipient.displayName.charAt(0).toUpperCase()
                  ) : (
                    <User className="h-3.5 w-3.5" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{recipient.displayName}</p>
                  <p className="text-xs text-slate-500 truncate">
                    {needsPhone
                      ? recipient.phoneE164
                        ? formatPhoneNumber(recipient.phoneE164)
                        : "No phone number"
                      : recipient.email || "No email"}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setRecipient(null);
                    setSelectedMatter("");
                    setClientMatters([]);
                    setShowMatterField(false);
                    setTimeout(() => inputRef.current?.focus(), 50);
                  }}
                  className="p-1 rounded-lg hover:bg-slate-200 transition-colors flex-shrink-0"
                >
                  <X className="h-4 w-4 text-slate-400" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                <input
                  ref={inputRef}
                  type="text"
                  placeholder={
                    needsPhone
                      ? "Search contacts or type a phone number..."
                      : needsEmail
                        ? "Search contacts or type an email..."
                        : "Search contacts..."
                  }
                  value={toInput}
                  onChange={(e) => setToInput(e.target.value)}
                  onFocus={() => (contacts.length > 0 || showManualOption) && setShowResults(true)}
                  onKeyDown={handleToKeyDown}
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
                  autoFocus
                />

                {/* Dropdown results */}
                {showResults && (toInput.trim().length >= 2 || showManualOption) && (
                  <div
                    ref={resultsRef}
                    className="absolute z-50 w-full mt-1 bg-white rounded-xl border border-slate-200 shadow-lg max-h-64 overflow-y-auto"
                  >
                    {/* Manual number/email option */}
                    {showManualOption && (
                      <button
                        onClick={() => applyManualInput(toInput)}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-emerald-50 border-b border-slate-100 transition-colors"
                      >
                        <div className="h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                          {isManualPhone ? (
                            <Phone className="h-3.5 w-3.5 text-emerald-600" />
                          ) : (
                            <Mail className="h-3.5 w-3.5 text-emerald-600" />
                          )}
                        </div>
                        <div className="text-left flex-1 min-w-0">
                          <p className="text-sm font-medium text-emerald-700">
                            {isManualPhone ? `Send to ${formatPhoneNumber(toE164(toInput.trim()))}` : `Send to ${toInput.trim()}`}
                          </p>
                          <p className="text-xs text-emerald-500">
                            {isManualPhone ? "Use this phone number" : "Use this email address"}
                          </p>
                        </div>
                      </button>
                    )}

                    {/* Contact results */}
                    {contacts.map((c) => {
                      const phone = c.phones?.[0]?.number;
                      const email = c.emails?.[0]?.email;
                      const subtitle = needsPhone
                        ? phone
                          ? formatPhoneNumber(phone)
                          : "No phone number"
                        : email || "No email";
                      const hasRequired = needsPhone ? !!phone : !!email;

                      return (
                        <button
                          key={c._id}
                          onClick={() => selectContact(c)}
                          className={`w-full flex items-center gap-3 px-4 py-3 border-b border-slate-50 last:border-b-0 transition-colors ${
                            hasRequired ? "hover:bg-slate-50" : "opacity-50 cursor-not-allowed"
                          }`}
                          disabled={!hasRequired}
                        >
                          <div
                            className="h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
                            style={{ backgroundColor: "var(--color-accent)" }}
                          >
                            {c.displayName.charAt(0).toUpperCase()}
                          </div>
                          <div className="text-left flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">{c.displayName}</p>
                            <p className={`text-xs truncate ${hasRequired ? "text-slate-500" : "text-red-400"}`}>
                              {subtitle}
                            </p>
                          </div>
                        </button>
                      );
                    })}

                    {/* Loading spinner */}
                    {contactLoading && contacts.length === 0 && !showManualOption && (
                      <div className="flex justify-center py-4">
                        <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />
                      </div>
                    )}

                    {/* No results */}
                    {!contactLoading && contacts.length === 0 && !showManualOption && toInput.trim().length >= 2 && (
                      <div className="px-4 py-4 text-center">
                        <p className="text-xs text-slate-400">No contacts found</p>
                        <p className="text-xs text-slate-400 mt-0.5">
                          {needsPhone ? "Type a phone number to send directly" : "Type an email to send directly"}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Warning if contact doesn't have required info */}
            {recipient && needsPhone && !recipient.phoneE164 && (
              <p className="text-xs text-amber-600 mt-1.5 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                This contact has no phone number. Select a different contact or type a number.
              </p>
            )}
            {recipient && needsEmail && !recipient.email && (
              <p className="text-xs text-amber-600 mt-1.5 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                This contact has no email address. Select a different contact or type an email.
              </p>
            )}
          </div>

          {/* From number (SMS / WhatsApp / Voice) */}
          {isPhoneChannel && twilioNumbers.length > 0 && (
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">From</label>
              {twilioNumbers.length === 1 ? (
                <div className="flex items-center gap-2 px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-700">
                  <Phone className="h-4 w-4 text-slate-400" />
                  {formatFromLabel(twilioNumbers[0])}
                </div>
              ) : (
                <SearchableSelect
                  options={twilioNumbers.map((n) => ({
                    value: n.phoneE164,
                    label: formatFromLabel(n),
                    icon: Phone,
                  }))}
                  value={selectedFromNumber}
                  onChange={(val) => setSelectedFromNumber(val || "")}
                  placeholder="Select your number"
                  icon={Phone}
                />
              )}
            </div>
          )}

          {/* No number yet: explain why Send is unavailable */}
          {isPhoneChannel && numbersLoaded && twilioNumbers.length === 0 && (
            <div className="flex items-start gap-3 px-4 py-3 rounded-xl border border-amber-200 bg-amber-50">
              <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-amber-800">
                  {channel === "voice" ? "No calling number yet" : "No texting number yet"}
                </p>
                <p className="text-xs text-amber-700 mt-0.5">
                  Get a firm phone number to {channel === "voice" ? "call" : "text"} clients from Kavoa.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    navigate("/settings/phone-numbers");
                  }}
                  className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#0F5132] hover:underline"
                >
                  Set up a phone number
                  <ArrowRight className="h-3 w-3" />
                </button>
              </div>
            </div>
          )}

          {/* From email (Email channel) */}
          {channel === "email" && emailConnections.length > 0 && (
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">From</label>
              {emailConnections.length === 1 ? (
                <div className="flex items-center gap-2 px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-700">
                  <Mail className="h-4 w-4 text-slate-400" />
                  {emailConnections[0].mailbox.email}
                </div>
              ) : (
                <SearchableSelect
                  options={emailConnections.map((ec) => ({
                    value: ec._id,
                    label: ec.mailbox.email,
                    icon: Mail,
                  }))}
                  value={selectedEmailConnectionId}
                  onChange={(val) => setSelectedEmailConnectionId(val || null)}
                  placeholder="Select email account"
                  icon={Mail}
                />
              )}
            </div>
          )}

          {/* Subject (Email only) */}
          {channel === "email" && (
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Subject</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
                placeholder="Email subject"
              />
            </div>
          )}

          {/* Link to Matter (optional, collapsible) */}
          {recipient?.contactId && (
            <div>
              {!showMatterField ? (
                <button
                  onClick={() => setShowMatterField(true)}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors"
                >
                  <Link2 className="h-3.5 w-3.5" />
                  Link to matter
                </button>
              ) : (
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                    Matter (optional)
                  </label>
                  {mattersLoading ? (
                    <div className="flex items-center gap-2 px-4 py-3 text-sm text-slate-400">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading matters...
                    </div>
                  ) : (
                    <SearchableSelect
                      options={matterOptions}
                      value={selectedMatter || null}
                      onChange={(val) => setSelectedMatter(val || "")}
                      placeholder={clientMatters.length === 0 ? "No matters for this client" : "Select a matter"}
                      icon={Briefcase}
                      addNew={{
                        label: "New Matter",
                        onAdd: () => setShowQuickMatter(true),
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          )}

          {/* Message body (not for voice) */}
          {channel !== "voice" ? (
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Message</label>
              <textarea
                value={messageBody}
                onChange={(e) => setMessageBody(e.target.value)}
                rows={3}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 resize-none transition-colors"
                placeholder={channel === "email" ? "Compose your email..." : "Type your message..."}
              />
            </div>
          ) : (
            <div className="px-4 py-3 rounded-xl bg-amber-50 border border-amber-200">
              <p className="text-xs text-amber-700 flex items-center gap-1.5">
                <PhoneCall className="h-3.5 w-3.5" />
                A voice call will be initiated to the recipient when you click Start Call.
              </p>
            </div>
          )}

          {/* Action button */}
          <button
            onClick={handleCreate}
            disabled={!canSend || creating}
            className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl text-white text-sm font-bold disabled:opacity-40 transition-colors"
            style={{ backgroundColor: "var(--color-primary)" }}
            onMouseEnter={(e) => {
              if (canSend && !creating) e.currentTarget.style.backgroundColor = "var(--color-primary-hover)";
            }}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
          >
            {creating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {channel === "voice" ? "Starting Call..." : "Sending..."}
              </>
            ) : channel === "voice" ? (
              <>
                <PhoneCall className="h-4 w-4" />
                Start Call
              </>
            ) : channel === "email" ? (
              <>
                <Mail className="h-4 w-4" />
                Open Compose
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                Start Conversation
              </>
            )}
          </button>
        </div>
      </PremiumModal>

      {/* Quick Create Matter */}
      <QuickCreateMatterModal
        open={showQuickMatter}
        onClose={() => setShowQuickMatter(false)}
        preselectedClientId={recipient?.contactId}
        onCreated={(newMatter) => {
          setClientMatters((prev) => [...prev, { _id: newMatter._id, title: newMatter.title }]);
          setSelectedMatter(newMatter._id);
          setShowQuickMatter(false);
        }}
      />
    </>
  );
};

export default NewConversationModal;
